// The reconnect retry loop, ported from
// `lib/features/connection/domain/reconnect_loop.dart`.
//
// THE NUMBERS ARE NOT ADJUSTABLE, and this is the one place in the renderer
// where that is worth stating twice:
//
//   Server rejoin grace .... 30s  (RejoinGraceMillis, match_loop.go)
//   Client retry window .... 28s  (this file's DEFAULT_WINDOW_MS)
//   Backoff ................ 1s, 2s, 3s, then capped at 4s
//   Per-attempt timeout .... 6s   (see attemptWithTimeout)
//
// The 28 sits deliberately just UNDER the 30 so the client never gives up
// while the server is still holding the player's seat. A shorter window is not
// a tuning choice, it is abandoning a player the server would have taken back:
// the original brief for this renderer specified a ~10s window, which would
// have forfeited every drop that lasted longer than that even though the
// server waits three times as long.
//
// Within the grace the server also still SCORES a late submission
// (lateSubmissionWindowMillis is derived from the same constant), so a player
// who gets back inside the window loses nothing at all.

/** Client retry window. Must stay below the server's 30s rejoin grace. */
export const DEFAULT_WINDOW_MS = 28_000;

/** Bound on a single rejoin attempt, so a hung connect cannot stall the loop. */
export const ATTEMPT_TIMEOUT_MS = 6_000;

export interface ReconnectLoopOptions {
  /** One rejoin attempt. Resolves true on success. Must not throw. */
  attempt: () => Promise<boolean>;
  /** Injected for tests. */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  windowMs?: number;
  /** Called before each attempt with the 1-based attempt number. */
  onAttempt?: (n: number) => void;
}

/**
 * Retries [attempt] with capped backoff until it succeeds or the window
 * elapses. Resolves true on success.
 *
 * Behaviour-identical to the Dart original, including the detail that the
 * FINAL wait is clamped to the remaining time so the loop never sleeps past
 * its own deadline and wastes the last usable seconds of the grace period.
 */
export async function runReconnectLoop(opts: ReconnectLoopOptions): Promise<boolean> {
  const now = opts.now ?? (() => Date.now());
  const sleep = opts.sleep ?? defaultSleep;
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;

  const deadline = now() + windowMs;
  let attemptNumber = 0;

  while (now() < deadline) {
    attemptNumber++;
    opts.onAttempt?.(attemptNumber);
    if (await opts.attempt()) return true;

    const remaining = deadline - now();
    if (remaining <= 0) break;
    // 1s, 2s, 3s, then capped at 4s — and never overshoot the window.
    const backoff = Math.min(Math.max(attemptNumber, 1), 4) * 1000;
    await sleep(Math.min(backoff, remaining));
  }

  return false;
}

/**
 * Races [work] against a timeout, resolving false rather than throwing.
 *
 * The Flutter client bounds each `retryJoinMatch` at 6s for a specific reason:
 * a socket connect while the network is flapping does not fail, it HANGS, and
 * a hung attempt with no bound eats the whole 28s window in one go.
 */
export async function attemptWithTimeout(
  work: () => Promise<boolean>,
  timeoutMs = ATTEMPT_TIMEOUT_MS,
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work().catch(() => false),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Single-flight guard around a reconnect.
 *
 * Every failed connect fires the socket's close handler again, so without this
 * a flapping network starts a NEW retry loop per failure and they interleave —
 * the Flutter client's `_isReconnecting` field exists for exactly this and the
 * bug it prevents (nested retry loops racing each other into a superseded
 * socket) is not theoretical.
 */
export class SingleFlight {
  private inFlight: Promise<boolean> | null = null;

  get isRunning(): boolean {
    return this.inFlight !== null;
  }

  /** Runs [work], or returns the already-running run's promise. */
  run(work: () => Promise<boolean>): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    const started = work().finally(() => {
      this.inFlight = null;
    });
    this.inFlight = started;
    return started;
  }

  /**
   * Clears the guard because success arrived by another route.
   *
   * The authoritative "we are back" signal is opCode 2 (`joinedMatch`), not
   * the retry loop's own return value — the Flutter client clears its guard on
   * that event as a fallback precisely because a loop can race a superseded
   * socket and miss its own success.
   */
  release(): void {
    this.inFlight = null;
  }
}
